// frontend/src/pages/MatchScoring/BowlerChangeModal.js
//
// Picks a bowler. Three situations:
//
//   1. END OF OVER (forced) — the over is complete; pick the next bowler.
//      The bowler who just finished can't bowl consecutive overs.
//
//   2. START OF OVER (not forced, nothing bowled yet this over) — a plain
//      re-pick: the over hasn't started, so it's still the over's opening
//      choice.
//
//   3. MID-OVER (not forced, deliveries already bowled this over) — a bowler
//      can't change mid-over except for injury and the like, so the scorer
//      must say which this is:
//        • "Wrong bowler was picked" — the earlier balls of this over are
//          moved to the chosen bowler (they were his all along). This is the
//          27 Sept case: wrong name tapped at the over change, noticed a ball
//          later.
//        • "Injury / other reason" — the chosen bowler finishes the over; the
//          balls already bowled stay with the first bowler.
//      The server refuses a mid-over ball by a different bowler unless one of
//      these has been done (handover 28 Sep §1.4).

import React, { useState } from 'react';
import api from '../../utils/api';
import { confirmCondition } from './conditions';

export default function BowlerChangeModal({
  matchId,
  inningsId,
  overNumber,             // the over in progress / about to start (legal_balls / 6)
  bowlingSquad,
  membersById,
  previousBowlerId,       // the bowler in live state right now
  bannedBowlerIds = [],
  ballsThisOver = 0,      // deliveries (legal or not) already in this over
  legalThisOver = 0,      // legal deliveries already in this over
  overBowlerId = null,    // who bowled this over's deliveries so far
  lastOverBowlerId = null,// who bowled the previous over
  forced = false,         // when true, can't be cancelled (end-of-over flow)
  limit = null,           // most overs one bowler may bowl this innings (null = no limit)
  oversBy = null,         // Map member_id -> overs bowled this innings
  conditionsById = null,  // Map member_id -> { no_bowling, no_keeping } for this match
  onCancel,
  onChanged,
}) {
  const [bowlerId, setBowlerId] = useState('');
  const [mode, setMode]         = useState('');   // 'correct' | 'injury' | 'other' (mid-over only)
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const midOver = !forced && ballsThisOver > 0;
  const nameOf  = (id) => membersById.get(id)?.display_name || (id ? id.slice(0, 8) : '—');

  // Nobody may bowl two overs running.
  //   End of over  — the bowler who just finished is out.
  //   Otherwise    — whoever bowled the PREVIOUS over is out. The bowler of
  //                  the over in progress is never excluded: on 4 Oct, after
  //                  an Undo left the wrong name on screen, he was the one
  //                  person the scorer needed and he was missing from the list.
  const excluded = new Set([
    ...(forced ? [previousBowlerId] : [lastOverBowlerId]),
    ...bannedBowlerIds,
  ].filter(Boolean));
  const eligible = bowlingSquad.filter(s => s.can_bowl !== false && !excluded.has(s.member_id));

  // Mid-over and the screen's bowler isn't the one bowling this over: the
  // obvious fix is one tap — carry on with him.
  const mismatch = midOver && overBowlerId && overBowlerId !== previousBowlerId;
  async function continueWithOverBowler() {
    setBusy(true); setErr(null);
    try {
      await api.post(`/scorer/matches/${matchId}/innings/${inningsId}/correct-over-bowler`, {
        over_number: overNumber ?? 0, to_bowler_id: overBowlerId,
      });
      onChanged?.(overBowlerId);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Could not set the bowler');
    } finally { setBusy(false); }
  }
  // Picking the over's own bowler mid-over needs no "what happened" — it's a
  // straight continue.
  const pickedOverBowler = midOver && bowlerId && bowlerId === overBowlerId;

  // Bowling limit: overs already bowled this innings, not counting the over
  // in progress (a bowler finishing his own over is never blocked).
  const usedOvers = (id) => {
    const n = oversBy?.get(id) || 0;
    return id === overBowlerId && ballsThisOver > 0 ? n - 1 : n;
  };
  const quotaDone = (id) => limit != null && usedOvers(id) >= limit && !(midOver && id === overBowlerId);

  async function go() {
    if (!bowlerId) return;
    if (quotaDone(bowlerId)) { setErr(`${nameOf(bowlerId)} has bowled his ${limit} overs.`); return; }
    if (!pickedOverBowler && !(await confirmCondition({ matchId, memberId: bowlerId, kind: 'bowling', name: nameOf(bowlerId),
      inningsId, overNumber: overNumber ?? 0, conditionsById }))) return;
    if (pickedOverBowler) return continueWithOverBowler();
    if (midOver && !mode) return;
    setBusy(true); setErr(null);
    try {
      if (midOver && mode === 'correct') {
        await api.post(`/scorer/matches/${matchId}/innings/${inningsId}/correct-over-bowler`, {
          over_number:  overNumber ?? 0,
          to_bowler_id: bowlerId,
        });
      } else {
        await api.post(`/scorer/matches/${matchId}/bowler-change`, {
          innings_id:     inningsId,
          over_number:    overNumber ?? 0,
          ball_in_over:   midOver ? Math.min(6, legalThisOver) : 0,
          from_bowler_id: previousBowlerId,
          to_bowler_id:   bowlerId,
          // Before the over's first delivery it is still the over's opening
          // pick; only a change after deliveries is a mid-over change.
          reason:         midOver ? mode : 'end_of_over',
        });
      }
      onChanged?.(bowlerId);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Bowler change failed');
    } finally {
      setBusy(false);
    }
  }

  const ballsWord = `${ballsThisOver} ball${ballsThisOver === 1 ? '' : 's'}`;
  const optStyle = (on) => ({
    display: 'block', width: '100%', textAlign: 'left', marginTop: 8,
    padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
    border: `2px solid ${on ? 'var(--sc-accent, #F5C518)' : 'var(--sc-border, #334155)'}`,
    background: on ? 'rgba(245,197,24,0.12)' : 'transparent',
    color: 'inherit', font: 'inherit',
  });

  return (
    <div
      className="sc-modal-backdrop"
      onClick={(e) => {
        // If forced, don't allow backdrop dismissal — captain MUST pick a bowler.
        if (forced) return;
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="sc-modal">
        <div className="sc-modal-body">
          <h3>{forced ? 'End of over — pick next bowler' : midOver ? 'Change bowler mid-over' : 'Change bowler'}</h3>
          {forced && (
            <div style={{ fontSize: 12, color: 'var(--sc-text-mute)', marginBottom: 10 }}>
              The over is complete. Pick the next bowler to continue.
            </div>
          )}
          {midOver && (
            <div style={{ fontSize: 12, color: 'var(--sc-text-mute)', marginBottom: 10 }}>
              Over {(overNumber ?? 0) + 1} has {ballsWord} so far, recorded against <strong>{nameOf(overBowlerId)}</strong>.
              A bowler can't change mid-over except for injury.
            </div>
          )}

          {mismatch && (
            <button onClick={continueWithOverBowler} disabled={busy}
              style={{ ...optStyle(true), marginBottom: 12, background: 'rgba(22,163,74,0.12)', borderColor: '#16a34a' }}>
              <strong>↩ Continue with {nameOf(overBowlerId)}</strong>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                He is bowling this over — set him as the bowler and carry on.
              </div>
            </button>
          )}

          <div className="sc-chip-row">
            {eligible.map(s => {
              const done = quotaDone(s.member_id);
              const nb = conditionsById?.get(s.member_id)?.no_bowling;
              return (
                <button
                  key={s.member_id}
                  className={`sc-chip ${bowlerId === s.member_id ? 'sc-active' : ''}`}
                  disabled={done}
                  title={done ? `Has bowled his ${limit} overs` : nb ? 'Said No bowling for this match' : ''}
                  style={done ? { opacity: 0.4, textDecoration: 'line-through' } : undefined}
                  onClick={() => setBowlerId(s.member_id)}
                >
                  {nameOf(s.member_id)}
                  {limit != null && <span style={{ fontSize: 11, opacity: 0.75 }}> {usedOvers(s.member_id)}/{limit}</span>}
                  {nb && !done && <span style={{ fontSize: 11, color: '#B45309' }}> ⚠</span>}
                </button>
              );
            })}
          </div>
          {limit != null && (
            <div style={{ fontSize: 11, color: 'var(--sc-text-mute)', marginTop: 6 }}>
              Each bowler can bowl {limit} overs this innings. ⚠ = said No bowling for this match.
            </div>
          )}
          <div>
          </div>

          {midOver && !pickedOverBowler && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>What happened?</div>
              <button style={optStyle(mode === 'correct')} onClick={() => setMode('correct')}>
                <strong>Wrong bowler was picked</strong>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {bowlerId ? nameOf(bowlerId) : 'The chosen bowler'} bowled this over from the start —
                  move the {ballsWord} to him.
                </div>
              </button>
              <button style={optStyle(mode === 'injury')} onClick={() => setMode('injury')}>
                <strong>Injury</strong>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {nameOf(overBowlerId)} keeps the {ballsWord}; {bowlerId ? nameOf(bowlerId) : 'the chosen bowler'} finishes the over.
                </div>
              </button>
              <button style={optStyle(mode === 'other')} onClick={() => setMode('other')}>
                <strong>Other reason</strong>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  Removed by the umpire or similar — same as injury for the figures.
                </div>
              </button>
            </div>
          )}

          {err && <div className="sc-error-box">{err}</div>}
        </div>{/* /sc-modal-body */}
        <div className="sc-modal-actions">
          <button className="sc-submit" disabled={!bowlerId || (midOver && !mode && !pickedOverBowler) || busy} onClick={go}>
            {busy ? 'Saving…' : pickedOverBowler ? `Continue with ${nameOf(bowlerId)}` : midOver && mode === 'correct' ? 'Correct the over' : 'Confirm bowler'}
          </button>
          {!forced && (
            <button className="sc-cancel" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
}
